import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { produce } from "immer";
import uuid from "uuid";

import {
  gql,
  GraphQLAPI,
  GraphQLResult,
  parseClassroom,
  parseSearchResults,
  queries,
  typedGql,
} from "@examspark/api";
import { formatResourceIdFieldValue } from "@examspark/api-ddb";
import {
  assertNativeError,
  delayForIndexing,
  filterMaybeArray,
  formatClassroomActivityKey,
  generateExamTicketCode,
  getTagFromResource,
  SystemTag,
} from "@examspark/common";
import { itemsGql } from "@examspark/common-app-search";
import {
  AuthorizationContextInput,
  BucketAggregation,
  Classroom,
  ClassroomActivity,
  CreateClassroomActivityInput,
  CreateResourceInput,
  ExamConfigData,
  IDSK,
  ModelResourceConnection,
  ParsedClassroom,
  ParsedSearchableResourceConnectionNew,
  QuerySearchArgs,
  Resource,
  SearchableResourceConnectionNew,
  TagInput,
  Unit,
} from "@examspark/types";

import { defaultExamConfigData } from "../../components/activities/activity-editor/hooks/useActivityEditor";
import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { useGoogleClassroom } from "../../hooks/useGoogleClassroom";
import { useGoogleSignIn } from "../../hooks/useGoogleSignIn";
import { useAppContext } from "../../providers/AppContext";

import { useMutationListClassroomStudents } from "./student";

/**
 * This operation creates:
 *  - `[code]/exam-ticket` for global lookup.
 *  - `[code]/exam-ticket#[examConfigId]` for local lookup
 * @param params
 * @returns
 */
export const useMutationCreateAccessCodes = (params: {
  activityId?: string;
  workspaceId?: string;
}) => {
  const client = useQueryClient();
  const { refetch: refetchTickets } = useQueryListActivityExamTickets({
    workspaceId: params.workspaceId,
    examConfigId: params.activityId,
  });
  const listClassroomStudents = useMutationListClassroomStudents();
  const createClassroomActivityTicket =
    useMutationCreateClassroomActivityTicket();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      classroomId,
      examConfigId,
      creatorUserId,
    }: {
      workspaceId: string;
      classroomId: string;
      examConfigId: string;
      creatorUserId: string;
    }) => {
      const students = await listClassroomStudents.mutateAsync({
        workspaceId: workspaceId,
        classroomId: classroomId,
      });

      const tickets = await refetchTickets();
      for (const student of filterMaybeArray(students ?? [])) {
        const [, studentId] = student.sk.split("#");
        const ticketId = generateExamTicketCode();

        // console.log("new tickets", tickets);

        // does a ticket already exist?
        const existing = tickets.data?.find((ticket) => {
          const ticketStudentId = getTagFromResource(
            ticket,
            "student-id",
          )?.value;
          return studentId === ticketStudentId;
        });
        if (existing) {
          console.info("ticket already exists", ticketId);
          continue;
        }

        const tags: TagInput[] = [
          {
            key: "student-id",
            type: "string",
            value: studentId,
          },
          {
            key: "classroom-id",
            type: "string",
            value: classroomId,
          },
        ];

        console.log("ticketId", ticketId);
        // this record enables global lookup when students enters access code
        const createGlobalTicket = createClassroomActivityTicket.mutateAsync({
          id: ticketId,
          sk: `exam-ticket`,
          schemaVersion: "2020-01-01",
          workspaceId: workspaceId,
          creatorId: creatorUserId,
          resourceId: [examConfigId, "exam-config"].join("/"),
          tags,
        });

        // this record enables listing capability within an `exam-config` create
        // exam-ticket#[examConfigId] for listing purposes
        const createLookupTicket = createClassroomActivityTicket.mutateAsync({
          id: ticketId,
          sk: `exam-ticket#${examConfigId}`,
          schemaVersion: "2020-01-01",
          workspaceId: workspaceId,
          creatorId: creatorUserId,
          resourceId: [examConfigId, "exam-config"].join("/"),
          tags,
        });

        await Promise.all([createGlobalTicket, createLookupTicket]);
      }

      client.invalidateQueries(["tickets"]);
    },
  });
};

/**
 * This operation creates:
 *  - `[code]/exam-ticket` for global lookup.
 *  - `[code]/exam-ticket#[examConfigId]` for local lookup
 * @param params
 * @returns
 */
export const useMutationCreateStudentAccessCode = () => {
  const createClassroomActivityTicket =
    useMutationCreateClassroomActivityTicket();

  return useMutation({
    mutationFn: async ({
      classroomId,
      creatorUserId,
      examConfigId,
      studentId,
      workspaceId,
    }: {
      classroomId: string;
      creatorUserId: string;
      examConfigId: string;
      studentId: string;
      workspaceId: string;
    }) => {
      const ticketId = generateExamTicketCode();

      const tags: TagInput[] = [
        {
          key: "student-id",
          type: "string",
          value: studentId,
        },
        {
          key: "classroom-id",
          type: "string",
          value: classroomId,
        },
      ];

      console.log("ticketId", ticketId);
      // this record enables global lookup when students enters access code
      const createGlobalTicket = createClassroomActivityTicket.mutateAsync({
        id: ticketId,
        sk: `exam-ticket`,
        schemaVersion: "2020-01-01",
        workspaceId: workspaceId,
        creatorId: creatorUserId,
        resourceId: [examConfigId, "exam-config"].join("/"),
        tags,
      });

      // this record enables listing capability within an `exam-config` create
      // exam-ticket#[examConfigId] for listing purposes
      const createLookupTicket = createClassroomActivityTicket.mutateAsync({
        id: ticketId,
        sk: `exam-ticket#${examConfigId}`,
        schemaVersion: "2020-01-01",
        workspaceId: workspaceId,
        creatorId: creatorUserId,
        resourceId: [examConfigId, "exam-config"].join("/"),
        tags,
      });

      await Promise.all([createGlobalTicket, createLookupTicket]);
    },
  });
};

export const useMutationDeleteStudentAccessCode = () => {
  return useMutation({
    mutationFn: async (params: {
      examConfigId: string;
      accessCode: string;
      workspaceId: string;
    }) => {
      const deleteGlobalTicket = await gql.operations.resource.deleteResource({
        id: params.accessCode,
        sk: `exam-ticket`,
        workspaceId: params.workspaceId,
      });

      const deleteLookupTicket = await gql.operations.resource.deleteResource({
        id: params.accessCode,
        sk: `exam-ticket#${params.examConfigId}`,
        workspaceId: params.workspaceId,
      });

      await Promise.all([deleteLookupTicket, deleteGlobalTicket]);
    },
  });
};

export const useMutationDeleteClassroomActivity = () => {
  const authorizationContext = useAuthorizationContext();

  return useMutation({
    mutationFn: async (params: { activityKey: IDSK }) => {
      const deleteActivity =
        await gql.operations.classroom.deleteClassroomActivity({
          input: {
            action: "deleteClassroomActivity",
            params: JSON.stringify(params),
          },
          authorizationContext,
        });

      return deleteActivity;
    },
  });
};

export const useQueryGetClassroomActivity = (
  classroomId?: string,
  skSuffix?: string,
) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery<ClassroomActivity>({
    enabled: !!classroomId && !!skSuffix,
    queryKey: ["classroom-activity", classroomId, skSuffix],
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.classroom.getClassroomActivity,
        variables: {
          input: {
            id: classroomId,
            sk: `classroom-activity#${skSuffix}`,
          },
          authorizationContext,
        },
      })) as any;

      return result.data.getClassroomActivity;
    },
  });
};

export const useQueryListClassroomUnitActivities = (
  workspaceId?: string,
  classroomId?: string,
  unitId?: string,
) => {
  return useQuery({
    enabled: !!workspaceId && !!classroomId && !!unitId,
    queryKey: ["classroom-activities", classroomId, unitId],
    queryFn: async () => {
      const result = await GraphQLAPI.query<{
        listClassroomActivities: ModelResourceConnection;
      }>({
        query: queries.classroom.listClassroomActivities,
        variables: {
          input: {
            id: classroomId,
            sk: {
              beginsWith: `${unitId}#`,
            },
            workspaceId,
          },
        },
      });

      return result.data?.listClassroomActivities.items;
    },
  });
};

export const useQueryListClassroomActivities = (
  workspaceId?: string,
  classroomId?: string,
) => {
  return useQuery({
    enabled: !!classroomId && !!workspaceId,
    queryKey: ["classroom-activities", classroomId],
    queryFn: async () => {
      const result = await typedGql(queries.classroom.listClassroomActivities)({
        input: {
          id: classroomId,
          sk: {
            beginsWith: ``,
          },
          workspaceId,
        },
      });

      return result.data?.listClassroomActivities.items;
    },
  });
};

export const useQuerySearchActivities = (params: { userId?: string }) => {
  const authorizationContext = useAuthorizationContext();
  return useQuery({
    queryKey: ["activities", params.userId],
    enabled: !!params.userId,
    queryFn: async () => {
      const result = (await GraphQLAPI.query<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "SearchActivities",
          fields: `
            id
            sk
            workspaceId
            resource {
              id
              sk
              name
            }
          `,
        }),
        variables: {
          filter: {
            sk: {
              wildcard: "classroom-activity*",
            },
            creatorId: {
              eq: params.userId,
            },
          },
          sort: [
            {
              field: "updatedAt",
              direction: "desc",
            },
          ],
          authorizationContext,
        },
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      const next = produce(result.data?.search, (draft) => {
        if (!draft) return;
        if (typeof draft.aggregations === "string") {
          draft.aggregations = JSON.parse(draft.aggregations);
        }
      }) as ParsedSearchableResourceConnectionNew;

      // console.log("next", next);
      return next;
    },
  });
};

export const useActivityBuilderSearch = () => {
  const authorizationContext = useAuthorizationContext();

  return useMutation({
    mutationFn: async (variables: QuerySearchArgs) => {
      const result = (await GraphQLAPI.query<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "BuildActivitySearch",
          fields: itemsGql,
        }),
        variables: {
          ...variables,
          authorizationContext,
        },
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      return parseSearchResults(result.data?.search);
    },
  });
};

export const useQuerySearchActivityObjectives = () => {
  const authorizationContext = useAuthorizationContext();
  const variables = {
    limit: 0,
    filter: {
      sk: {
        eq: "item#v0",
      },
      isDeleted: {
        ne: true,
      },
    },
    aggsNew: [
      {
        name: "objective",
        type: "terms",
        field: "objectiveId",
      },
    ],
  };

  return useQuery({
    queryKey: ["search", "activity-objectives"],
    queryFn: async () => {
      const result = (await GraphQLAPI.query<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "BuildActivitySearch",
          fields: itemsGql,
        }),
        variables: {
          ...variables,
          authorizationContext,
        },
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      return (parseSearchResults(result.data?.search).aggregations?.objective
        .buckets ?? []) as BucketAggregation[];
    },
  });
};

const searchActivityObjectivesByUnit = (
  unit: Unit,
  authorizationContext: AuthorizationContextInput,
) => {
  const variables = {
    limit: 0,
    filter: {
      referencesIds: {
        eq: unit.id,
      },
      sk: {
        eq: "item#v0",
      },
      isDeleted: {
        ne: true,
      },
    },
    aggsNew: [
      {
        name: "objective",
        type: "terms",
        field: "objectiveId",
      },
    ],
  };

  return {
    queryKey: ["search", "activity-objectives", unit.id],
    queryFn: async () => {
      const result = (await GraphQLAPI.query<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "BuildActivitySearch",
          fields: itemsGql,
        }),
        variables: {
          ...variables,
          authorizationContext,
        },
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      return (parseSearchResults(result.data?.search).aggregations?.objective
        .buckets ?? []) as BucketAggregation[];
    },
  };
};

export const useQueriesSearchActivityObjectivesByUnit = (units: Unit[]) => {
  const authorizationContext = useAuthorizationContext();

  return useQueries({
    queries: units.map((unit) =>
      searchActivityObjectivesByUnit(unit, authorizationContext),
    ),
  });
};

export const useMutationCreateClassroomActivity = () => {
  return useMutation({
    mutationFn: async (input: CreateClassroomActivityInput) => {
      const result = (await GraphQLAPI.query({
        query: queries.resource.createResource({
          label: "CreateClassroomActivity",
        }),
        variables: {
          input,
        },
      })) as GraphQLResult<{ createResource: Resource }>;

      return result.data?.createResource;
    },
  });
};

export const useMutationCreateClassroomActivityTicket = () => {
  return useMutation({
    mutationFn: async (input: CreateResourceInput) => {
      const result = (await GraphQLAPI.query({
        query: queries.resource.createResource({
          label: "CreateClassroomActivityTicket",
        }),
        variables: {
          input,
        },
      })) as GraphQLResult<{ createResource: Resource }>;

      return result.data?.createResource;
    },
  });
};

export const useQueryListActivityExamTickets = (params: {
  workspaceId?: string;
  examConfigId?: string;
}) => {
  return useQuery({
    enabled: !!params.workspaceId && !!params.examConfigId,
    queryKey: ["tickets", params.workspaceId, params.examConfigId],
    queryFn: async () => {
      const result = (await GraphQLAPI.query<{
        listResourcesByWorkspaceId: ModelResourceConnection;
      }>({
        query: /* GraphQL */ `
          query ListActivityTickets(
            $workspaceId: String
            $sk: ModelStringKeyConditionInput
            $sortDirection: ModelSortDirection
            $filter: ModelResourceFilterInput
            $limit: Int
            $nextToken: String
          ) {
            listResourcesByWorkspaceId(
              workspaceId: $workspaceId
              sk: $sk
              sortDirection: $sortDirection
              filter: $filter
              limit: $limit
              nextToken: $nextToken
            ) {
              items {
                id
                sk
                tags {
                  key
                  type
                  value
                }
              }
              nextToken
            }
          }
        `,
        variables: {
          workspaceId: params.workspaceId,
          sk: {
            eq: `exam-ticket#${params.examConfigId}`,
          },
        },
      })) as any;

      return result.data?.listResourcesByWorkspaceId?.items as Resource[];
    },
  });
};

/**
 * Assign an `exam` to a classroom.
 *
 * This is the more universal way to assign work to a classroom. Lowman is a bit
 * different. If you are in lowman, go see
 * `useMutationCreateClassroomActivityFromExam` in `operations/lowman.ts`
 */
export const useMutationAddExamToClassroom = () => {
  const [context] = useAppContext();
  const queryClient = useQueryClient();
  const createClassroomActivityMutation = useMutationCreateClassroomActivity();

  const gc = useGoogleClassroom({
    tokenServerUrl:
      process.env.REACT_APP_REST_API_URL ?? "https://api.examspark.com",
  });
  const gsi = useGoogleSignIn({
    tokenServerUrl:
      process.env.REACT_APP_REST_API_URL ?? "https://api.examspark.com",
  });

  const assignToGoogleClassroomCourse = async (
    classroom: ParsedClassroom,
    examConfig: IDSK & { resource?: Pick<Resource, "name"> },
  ) => {
    try {
      const refreshToken = classroom.data?.googleClassroom.auth.refreshToken;
      const courseId = classroom.data?.googleClassroom.course.id;

      if (!refreshToken) throw new Error("no refresh token");
      if (!courseId) throw new Error("no refresh token");
      const accessToken = await gsi.refreshAccessToken(refreshToken);
      if (!accessToken) throw new Error("no access token");
      gc.setToken(accessToken.access_token);

      const coursework = await gc.createCourseWork(courseId, {
        title: examConfig.resource?.name,
        description: "",
        examConfigId: examConfig.id,
      });

      console.log("coursework", coursework);
      return coursework;
    } catch (error) {
      assertNativeError(error);
      console.error(error);
    }
  };

  return useMutation({
    mutationFn: async (params: {
      exam: IDSK;
      classroom: IDSK;
      classroomUnit?: IDSK;
      name: string;
      config?: ExamConfigData;
      useOriginalExam?: boolean;
      ltiContextId?: string;
    }) => {
      const {
        classroom,
        classroomUnit,
        exam,
        ltiContextId,
        name,
        useOriginalExam,
      } = params;

      const examConfigId = uuid();
      const workspaceId = context.workspace?.id;
      const userId = context.user?.id;
      const appKeyId = context.appLicense?.appKey;
      const appId = context.appLicense?.appId;

      // we're still not quite good enough at setting this on new
      // workspaces. Pretend it's fine for now
      let bankId = context.appLicenseConfig?.userContentBankId;
      if (!bankId) {
        console.error(
          `Missing userContentBankId in ${workspaceId}. Using default.`,
        );
        bankId = `${workspaceId}/user-content`;
      }

      if (!workspaceId || !userId || !appKeyId || !appId || !bankId) {
        console.error({ workspaceId, userId, appKeyId, appId, bankId });
        throw new Error("Missing required fields");
      }

      const _unparsedClassroom = await gql.operations.classroom.getClassroom(
        classroom.id,
      );
      const parsedClassroom = parseClassroom(
        _unparsedClassroom as Classroom | undefined,
      );

      if (!parsedClassroom) {
        throw new Error("Failed to find or parse classroom");
      }

      // If we're creating for google classroom, we need to ensure the activity is
      // successfully created there first
      const gcCourse = parsedClassroom.data?.googleClassroom?.course;
      let coursework: gapi.client.classroom.CourseWork | undefined;
      if (parsedClassroom.data?.platform === "GOOGLE_CLASSROOM" && !!gcCourse) {
        coursework = await assignToGoogleClassroomCourse(parsedClassroom, {
          id: examConfigId,
          sk: "exam-config",
          resource: {
            name: name,
          },
        });

        if (!coursework) {
          throw new Error("Failed to create coursework in Google Classroom");
        }
      }

      const examConfigConfig: ExamConfigData = {
        ...defaultExamConfigData,
        ...context.app?.config?.examConfigDefaults,
        ...params.config,
      };

      const refreshToken =
        parsedClassroom.data?.googleClassroom.auth.refreshToken;

      if (parsedClassroom.data?.platform === "GOOGLE_CLASSROOM" && coursework) {
        examConfigConfig.googleClassroomAssignments = [
          {
            /**
             * @todo Temporary way to get refresh token over to `<LaunchExam />` we
             * want to refactor this before we go live. */
            refreshToken: refreshToken,
            courseWorkId: coursework.id ?? "",
            course: {
              id: gcCourse?.id ?? "",
              name: gcCourse?.name ?? "",
              section: gcCourse?.section ?? "",
              room: gcCourse?.room ?? "",
            },
          },
        ];
      }
      const examConfigTags = [
        {
          key: "classroom-id",
          value: classroom.id,
          type: "string",
        },
        {
          key: "cohort-classroom",
          value: classroom.id,
          type: "string",
        },
        {
          key: "cohort-school",
          value: "school-xxx-id",
          type: "string",
        },
      ];
      if (ltiContextId) {
        examConfigTags.push({
          key: SystemTag.LtiContextId,
          value: ltiContextId,
          type: "string",
        });
      }
      if (classroomUnit) {
        examConfigTags.push({
          key: "classroom-unit-id",
          value: classroomUnit.id,
          type: "string",
        });
      }

      const createExamConfigResult =
        await gql.operations.exam.createExamFromExamTemplate(
          exam,
          {
            id: examConfigId,
            workspaceId,
            creatorId: userId,
            appId,
            appKey: appKeyId,
            bankId,
            config: JSON.stringify(examConfigConfig),
            tags: examConfigTags,
            name: name,
          },
          {
            createAccessCode: true,
            useOriginalExam,
            ltiContextId,
          },
        );
      if (!createExamConfigResult) {
        console.error("No examConfig found");
        return;
      }

      const classroomUnitId = classroomUnit?.id;
      const examConfig = createExamConfigResult;

      // create `classroom-activity` record
      const createClassroomActivityResult =
        await createClassroomActivityMutation.mutateAsync({
          ...formatClassroomActivityKey(
            classroom.id,
            examConfig.id,
            classroomUnitId,
          ),
          schemaVersion: "2020-01-01",
          workspaceId: workspaceId,
          creatorId: userId,
          resourceId: formatResourceIdFieldValue(examConfig),
        });

      if (!createClassroomActivityResult) {
        console.error("No createClassroomActivityResult found");
        return;
      }

      await delayForIndexing();
      queryClient.invalidateQueries({
        queryKey: ["user-exam-configs"],
      });
      queryClient.invalidateQueries({
        queryKey: ["exams"],
      });
      queryClient.setQueryData(
        ["classroom-assignment-count-by-unit", classroom.id],
        (prev?: Record<string, number>) => {
          if (!prev) return prev;
          const next = produce(prev, (draft) => {
            if (!classroomUnitId) return;
            draft[classroomUnitId] = (draft[classroomUnitId] ?? 0) + 1;
          });
          console.log("next", next);
          return next;
        },
      );

      return {
        id: examConfigId,
        sk: "exam-config",
      };
    },
  });
};
